import { createContext, useContext, useEffect, useState } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

type AppRole = "admin" | "angler" | "prospect" | "superadmin";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  isProspect: boolean;
  isVerifiedMember: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  isAdmin: false,
  isSuperAdmin: false,
  isProspect: false,
  isVerifiedMember: false,
  signOut: async () => {},
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState<AppRole[]>([]);
  const [rolesFetched, setRolesFetched] = useState(false);

  const fetchUserRoles = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userId);

      if (error) {
        console.error("Error fetching user roles:", error);
        return;
      }

      setRoles((data || []).map(r => r.role as AppRole));
    } catch (error) {
      console.error("Error fetching user roles:", error);
    } finally {
      setRolesFetched(true);
    }
  };

  useEffect(() => {
    let initialSessionHandled = false;

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
        
        // Skip if getSession already handled the initial load
        if (!initialSessionHandled) return;

        if (session?.user) {
          setTimeout(() => fetchUserRoles(session.user.id), 0);
        } else {
          setRoles([]);
          setRolesFetched(true);
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      initialSessionHandled = true;
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      
      if (session?.user) {
        fetchUserRoles(session.user.id);
      } else {
        setRolesFetched(true);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
    setRoles([]);
  };

  // Role checks
  const isSuperAdmin = roles.includes("superadmin");
  const isAdmin = roles.includes("admin") || isSuperAdmin;
  const isProspect = roles.includes("prospect") && !roles.includes("angler") && !roles.includes("admin") && !isSuperAdmin;
  const isVerifiedMember = roles.includes("angler") || roles.includes("admin") || isSuperAdmin;

  return (
    <AuthContext.Provider value={{ 
      user, 
      session, 
      loading, 
      isAdmin,
      isSuperAdmin,
      isProspect,
      isVerifiedMember,
      signOut 
    }}>
      {children}
    </AuthContext.Provider>
  );
};
